import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Chip,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TuneIcon from "@mui/icons-material/Tune";
import {
  FiltrosAvancadosModal,
  FiltrosAvancados,
} from "../FiltrosAvancadosModal";
import {
  montarFiltrosAtivos,
  ValoresFiltros,
} from "../resumoFiltros";

interface Props {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  status: string;
  setStatus: (value: string) => void;
  servidor: string;
  setServidor: (value: string) => void;
  sexo: string;
  setSexo: (value: string) => void;
  zonaRural: string;
  setZonaRural: (value: string) => void;
  idadeMin: string;
  setIdadeMin: (value: string) => void;
  idadeMax: string;
  setIdadeMax: (value: string) => void;
  profissao: string;
  setProfissao: (value: string) => void;
  bairro: string;
  setBairro: (value: string) => void;
  cadastroInicio: string;
  setCadastroInicio: (value: string) => void;
  cadastroFim: string;
  setCadastroFim: (value: string) => void;
  resetFiltros: () => void;
}

export const FiltrosUsuarios: React.FC<Props> = (props) => {
  const {
    searchTerm, setSearchTerm,
    status, setStatus,
    servidor, setServidor,
    sexo, setSexo,
    zonaRural, setZonaRural,
    idadeMin, setIdadeMin,
    idadeMax, setIdadeMax,
    profissao, setProfissao,
    bairro, setBairro,
    cadastroInicio, setCadastroInicio,
    cadastroFim, setCadastroFim,
    resetFiltros,
  } = props;

  const isMobile = useMediaQuery("(max-width: 768px)");
  const [modalAberto, setModalAberto] = useState(false);

  // mapa campo -> setter (para remover um chip individual)
  const setters: Record<keyof ValoresFiltros, (v: any) => void> = {
    searchTerm: setSearchTerm,
    status: setStatus,
    servidor: setServidor,
    sexo: setSexo,
    zonaRural: setZonaRural,
    idadeMin: setIdadeMin,
    idadeMax: setIdadeMax,
    profissao: setProfissao,
    bairro: setBairro,
    cadastroInicio: setCadastroInicio,
    cadastroFim: setCadastroFim,
  };

  const valores: ValoresFiltros = {
    searchTerm, status, servidor, sexo, zonaRural,
    idadeMin, idadeMax, profissao, bairro, cadastroInicio, cadastroFim,
  };

  const filtrosAtivos = montarFiltrosAtivos(valores);

  // quantos filtros AVANÇADOS estão ativos (para o badge do botão)
  const qtdAvancados = filtrosAtivos.filter(
    (f) => f.id !== "busca" && f.id !== "status"
  ).length;

  const aplicarReset = (reset: Partial<ValoresFiltros>) => {
    (Object.keys(reset) as (keyof ValoresFiltros)[]).forEach((k) =>
      setters[k](reset[k])
    );
  };

  const valoresAvancados: FiltrosAvancados = {
    servidor, sexo, zonaRural, idadeMin, idadeMax, profissao, bairro,
    cadastroInicio, cadastroFim,
  };

  const aplicarAvancados = (v: FiltrosAvancados) => {
    setServidor(v.servidor);
    setSexo(v.sexo);
    setZonaRural(v.zonaRural);
    setIdadeMin(v.idadeMin);
    setIdadeMax(v.idadeMax);
    setProfissao(v.profissao);
    setBairro(v.bairro);
    setCadastroInicio(v.cadastroInicio);
    setCadastroFim(v.cadastroFim);
  };

  return (
    <Paper
      elevation={0}
      sx={{ p: 2, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2 }}
    >
      {/* Linha principal: busca + status + filtros avançados + limpar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <TextField
          placeholder="Pesquisar por nome, CPF ou CNPJ"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth={isMobile}
          size="small"
          sx={{ flex: 1, minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="todos">Todos</MenuItem>
          <MenuItem value="ativos">Ativos</MenuItem>
          <MenuItem value="desativados">Desativados</MenuItem>
        </TextField>

        <Badge badgeContent={qtdAvancados} color="primary">
          <Button
            variant="outlined"
            startIcon={<TuneIcon />}
            onClick={() => setModalAberto(true)}
            sx={{ height: 40, borderRadius: 2 }}
          >
            Filtros avançados
          </Button>
        </Badge>

        <Button
          variant="text"
          color="primary"
          onClick={resetFiltros}
          startIcon={<ClearIcon />}
          sx={{ height: 40 }}
        >
          Limpar filtros
        </Button>
      </Box>

      {/* Resumo dos filtros ativos (chips removíveis) */}
      {filtrosAtivos.length > 0 && (
        <Box
          sx={{
            mt: 1.5,
            pt: 1.5,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
            Filtros:
          </Typography>
          {filtrosAtivos.map((f) => (
            <Chip
              key={f.id}
              label={f.texto}
              size="small"
              onDelete={() => aplicarReset(f.reset)}
              sx={{ fontWeight: 600 }}
            />
          ))}
          {filtrosAtivos.length > 1 && (
            <Button
              size="small"
              color="error"
              onClick={resetFiltros}
              sx={{ fontWeight: 700, minWidth: 0, px: 1 }}
            >
              Limpar tudo
            </Button>
          )}
        </Box>
      )}

      <FiltrosAvancadosModal
        open={modalAberto}
        valores={valoresAvancados}
        onAplicar={aplicarAvancados}
        onFechar={() => setModalAberto(false)}
      />
    </Paper>
  );
};
